import React, { useRef, useState } from "react";
import {
  Box,
  Divider,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
} from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "@fontsource/kumbh-sans";

// ===== Convert Amount to Words =====
const convertNumberToWords = (num) => {
  if (num === 0) return "Zero";
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000)
      return (
        a[Math.floor(n / 100)] +
        " Hundred " +
        (n % 100 ? "and " + inWords(n % 100) : "")
      );
    if (n < 100000)
      return (
        inWords(Math.floor(n / 1000)) +
        " Thousand " +
        (n % 1000 ? inWords(n % 1000) : "")
      );
    if (n < 10000000)
      return (
        inWords(Math.floor(n / 100000)) +
        " Lakh " +
        (n % 100000 ? inWords(n % 100000) : "")
      );
    return (
      inWords(Math.floor(n / 10000000)) +
      " Crore " +
      (n % 10000000 ? inWords(n % 10000000) : "")
    );
  };
  return inWords(Math.round(num));
};

export default function Payslip() {
  const payslipRef = useRef();

  // ===== States =====
  const [logo, setLogo] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [showPF, setShowPF] = useState(false);
  const [showESI, setShowESI] = useState(false);
  const [showUAN, setShowUAN] = useState(false);

  const [meta, setMeta] = useState({
    companyName: "Brand Name Here!",
    address: "Address here",
    month: "",
  });

  const [empInfo, setEmpInfo] = useState({
    empCode: "",
    name: "",
    designation: "",
    department: "",
    doj: "",
    payableDays: "",
    aadhar: "",
  });

  const [bankInfo, setBankInfo] = useState({
    location: "",
    bankName: "",
    accountNo: "",
    pfNo: "",
    uan: "",
    esiNo: "",
    pan: "",
  });

  const [earnings, setEarnings] = useState([
    { desc: "Basic", amount: "1000" },
    { desc: "House Rent Allowance", amount: "1000" },
    { desc: "Conv Allowance", amount: "500" },
    { desc: "Other Allowance", amount: "500" },
    { desc: "Medical Allowance", amount: "500" },
    { desc: "Statutory Bonus", amount: "500" },
    { desc: "L. ENCASH", amount: "500" },
  ]);
  const [deductions, setDeductions] = useState([
    { desc: "Professional Tax", amount: "1000" },
    { desc: "Misc Deduction", amount: "1000" },
  ]);

  // ===== Handlers =====
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setLogo(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAddRow = (setFn, arr) =>
    setFn([...arr, { desc: "", amount: "" }]);
  const handleRemoveRow = (setFn, arr, index) =>
    setFn(arr.filter((_, i) => i !== index));

  const totalEarnings = earnings.reduce(
    (sum, e) => sum + parseFloat(e.amount || 0),
    0
  );
  const totalDeductions = deductions.reduce(
    (sum, d) => sum + parseFloat(d.amount || 0),
    0
  );
  const netPay = totalEarnings - totalDeductions;

  // ===== PDF Download =====
  //   const handleDownload = async () => {
  //     const element = payslipRef.current;
  //     element.classList.add("no-shadow"); // Temporarily remove shadow for export
  //     const canvas = await html2canvas(element, { scale: 3, useCORS: true });
  //     const imgData = canvas.toDataURL("image/png", 1.0);
  //     const pdf = new jsPDF("p", "mm", "a4");
  //     const width = 210;
  //     const height = 297;
  //     pdf.addImage(imgData, "PNG", 0, 0, width, height, "", "FAST");

  //     const monthLabel = new Date(meta.month).toLocaleString("default", {
  //       month: "short",
  //       year: "numeric",
  //     });
  //     const fileName = `Payslip_${monthLabel}_${empInfo.name || "Employee"}.pdf`;
  //     pdf.save(fileName);
  //     element.classList.remove("no-shadow");
  //   };

  const handleDownload = async () => {
    const element = payslipRef.current;
    element.classList.add("no-shadow");

    // Clone for rendering
    const clone = element.cloneNode(true);
    document.body.appendChild(clone);

    // Define exact A4 size in pixels (portrait)
    const A4_WIDTH_PX = 794;
    const A4_HEIGHT_PX = 1123;

    // Apply pixel-based layout styles
    Object.assign(clone.style, {
      width: `${A4_WIDTH_PX}px`,
      height: `${A4_HEIGHT_PX}px`,
      position: "fixed",
      left: "-9999px",
      top: "0",
      overflow: "hidden",
      boxSizing: "border-box",
      transform: "none",
      zoom: "1",
    });

    // Enforce box sizing for all children
    clone.querySelectorAll("*").forEach((node) => {
      node.style.maxWidth = "100%";
      node.style.boxSizing = "border-box";
    });

    // Slightly increase capture width to prevent right-edge clipping
    const canvas = await html2canvas(clone, {
      scale: 3,
      width: A4_WIDTH_PX, // Safe margin
      windowWidth: A4_WIDTH_PX,
      height: A4_HEIGHT_PX,
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
    });

    // Convert to PDF
    const imgData = canvas.toDataURL("image/png", 1.0);
    const pdf = new jsPDF("p", "mm", "a4");
    pdf.addImage(imgData, "PNG", 0, 0, 210, 297, "", "FAST");

    // File name based on month + employee name
    const monthLabel = new Date(meta.month).toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
    pdf.save(`Payslip_${monthLabel}_${empInfo.name || "Employee"}.pdf`);

    // Cleanup
    document.body.removeChild(clone);
    element.classList.remove("no-shadow");
  };

  // ===== UI =====
  return (
    <Box
      sx={{
        fontFamily: "'Kumbh Sans', sans-serif",
        bgcolor: "#86c8ffff",
        maxWidth: "100vw",
        height: { xs: "100%", md: "100vh" },
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 5,
          pt: 2,
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          whiteSpace={"nowrap"}
          sx={{
            color: "#1B76D2",
            textShadow: "1px 1px 2px rgba(0, 0, 0, 0.4)",
          }}
        >
          Payslip Generator
        </Typography>
        {/* Download Button */}
        <Box sx={{ textAlign: "center", display: { xs: "none", md: "block" } }}>
          <Button
            variant="contained"
            onClick={handleDownload}
            sx={{ px: 5, py: 1.3, fontWeight: "bold" }}
          >
            Download PDF
          </Button>
        </Box>
      </Box>
      {/* left and right panels */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "center", md: "flex-start" },
          justifyContent: { xs: "center", md: "space-around" }, // ✅ evenly distribute
          gap: 3,
          width: "100%",
        }}
      >
        {/* ========== LEFT PANEL ========== */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            height: "100%",
          }}
        >
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: 2,
              p: 3,
              // maxHeight: "80vh",
              height: { xs: "100%", md: "75vh" },
              overflowY: "auto",
              // position: "sticky",
              // top: "10px",
              // width: { xs: "100%", md: "40%" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                flexWrap: "wrap",
                gap: "20px",
                alignItems: "flex-start",
              }}
            >
              {/* Logo Upload */}
              <Box sx={{ mt: 2, width: "250px" }}>
                <Typography fontWeight={800} gutterBottom>
                  Company Logo
                </Typography>
                <Button variant="outlined" component="label" size="small">
                  Upload Logo
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleLogoUpload}
                  />
                </Button>
                {logo && (
                  <Box mt={1}>
                    <img
                      src={logo}
                      alt="logo"
                      style={{
                        height: 80,
                        borderRadius: 4,
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* Company Info */}

              <Box>
                <Typography fontWeight="bold" sx={{ mt: 2 }}>
                  Company Info
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <TextField
                    label="Brand Name"
                    size="small"
                    margin="dense"
                    variant="standard"
                    sx={{ width: "250px", px: 1 }}
                    value={meta.companyName}
                    onChange={(e) =>
                      setMeta({ ...meta, companyName: e.target.value })
                    }
                  />

                  <TextField
                    label="Address"
                    size="small"
                    margin="dense"
                    variant="standard"
                    multiline
                    sx={{ width: "250px", px: 1 }}
                    rows={2}
                    value={meta.address}
                    onChange={(e) =>
                      setMeta({ ...meta, address: e.target.value })
                    }
                  />
                  <TextField
                    label="Payslip Month"
                    size="small"
                    margin="dense"
                    variant="standard"
                    type="month"
                    sx={{ width: "250px", px: 1 }}
                    value={meta.month}
                    onChange={(e) =>
                      setMeta({ ...meta, month: e.target.value })
                    }
                  />
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { md: "row", xs: "column" },
                flexWrap: "wrap",
              }}
            >
              {/* Employee Info */}
              <Box>
                <Typography fontWeight="bold" sx={{ mt: 3 }}>
                  Employee Info
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {Object.entries(empInfo).map(([k, v]) => (
                    <TextField
                      key={k}
                      label={k
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                      size="small"
                      variant="standard"
                      margin="dense"
                      sx={{ width: "250px", px: 1 }}
                      value={v}
                      onChange={(e) =>
                        setEmpInfo({ ...empInfo, [k]: e.target.value })
                      }
                    />
                  ))}
                </Box>
              </Box>

              {/* Bank Info */}
              <Box>
                <Typography fontWeight="bold" sx={{ mt: 3 }}>
                  Bank / ID Info
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {Object.entries(bankInfo)
                    .filter(([k]) => {
                      if (k === "pfNo" && !showPF) return false;
                      if (k === "uan" && !showUAN) return false;
                      if (k === "esiNo" && !showESI) return false;
                      return true;
                    })
                    .map(([k, v]) => (
                      <TextField
                        key={k}
                        label={k
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())}
                        size="small"
                        margin="dense"
                        variant="standard"
                        value={v}
                        sx={{ width: "250px", px: 1 }}
                        onChange={(e) =>
                          setBankInfo({ ...bankInfo, [k]: e.target.value })
                        }
                      />
                    ))}
                </Box>
              </Box>
            </Box>

            {/* Earnings */}
            <Typography fontWeight="bold" sx={{ mt: 3 }}>
              Earnings
            </Typography>
            {earnings.map((row, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 1,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <TextField
                  label="Description"
                  size="small"
                  variant="standard"
                  sx={{ width: "250px" }}
                  value={row.desc}
                  onChange={(e) =>
                    setEarnings(
                      earnings.map((r, idx) =>
                        idx === i ? { ...r, desc: e.target.value } : r
                      )
                    )
                  }
                />
                <Box
                  sx={{
                    display: "flex",
                  }}
                >
                  <TextField
                    label="Amount"
                    size="small"
                    type="number"
                    value={row.amount}
                    onChange={(e) =>
                      setEarnings(
                        earnings.map((r, idx) =>
                          idx === i ? { ...r, amount: e.target.value } : r
                        )
                      )
                    }
                  />
                  <Button
                    color="error"
                    size="small"
                    onClick={() => handleRemoveRow(setEarnings, earnings, i)}
                  >
                    ✕
                  </Button>
                </Box>
              </Box>
            ))}
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleAddRow(setEarnings, earnings)}
            >
              + Add Earning
            </Button>

            {/* Deductions */}
            <Typography fontWeight="bold" sx={{ mt: 3 }}>
              Deductions
            </Typography>
            {deductions.map((row, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 1,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <TextField
                  label="Description"
                  size="small"
                  variant="standard"
                  sx={{ width: "250px" }}
                  value={row.desc}
                  onChange={(e) =>
                    setDeductions(
                      deductions.map((r, idx) =>
                        idx === i ? { ...r, desc: e.target.value } : r
                      )
                    )
                  }
                />
                <Box
                  sx={{
                    display: "flex",
                  }}
                >
                  <TextField
                    label="Amount"
                    size="small"
                    type="number"
                    value={row.amount}
                    onChange={(e) =>
                      setDeductions(
                        deductions.map((r, idx) =>
                          idx === i ? { ...r, amount: e.target.value } : r
                        )
                      )
                    }
                  />
                  <Button
                    color="error"
                    size="small"
                    onClick={() =>
                      handleRemoveRow(setDeductions, deductions, i)
                    }
                  >
                    ✕
                  </Button>
                </Box>
              </Box>
            ))}
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleAddRow(setDeductions, deductions)}
            >
              + Add Deduction
            </Button>

            {/* Toggles */}
            <Box sx={{ mt: 3, flexDirection: "column", display: "flex" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showPF}
                    onChange={() => setShowPF(!showPF)}
                  />
                }
                label="Show PF"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showESI}
                    onChange={() => setShowESI(!showESI)}
                  />
                }
                label="Show ESI"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showUAN}
                    onChange={() => setShowUAN(!showUAN)}
                  />
                }
                label="Show UAN"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showSignature}
                    onChange={() => setShowSignature(!showSignature)}
                  />
                }
                label="Show Signature & Date"
              />
            </Box>
          </Box>
        </Box>

        {/* ========== RIGHT PANEL ========== */}
        <Box
          sx={{
            width: { xs: "95%", md: "40%" },
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            gap: 1,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              // bgcolor: "#f50000ff",
              borderRadius: 2,
              // maxHeight: "80vh",
              // height: "80vh",
              // overflowY: "auto",
              // position: "sticky",
              // top: "10px",
              // width: { xs: "100%", md: "40%" },
              maxWidth: "476.4px",
              minWidth: "476.4px", // 794px * 0.7 ≈ 556px
              // maxHeight: "1123px",
              maxHeight: "673.8px",
            }}
          >
            {/* Display of Payslip */}
            <Paper
              ref={payslipRef}
              sx={{
                width: "794px",
                height: "1123px",
                // width: "210mm",
                // minHeight: "297mm", // smaller visible size
                maxHeight: "297mm",
                p: 4,
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 0 20px rgba(0,0,0,0.15)",
                "&.no-shadow": { boxShadow: "none" },
                display: "flex",
                flexDirection: "column",
                // transform: "scale(0.8)",
                transform: "scale(0.6)",
                transformOrigin: "top left",

                // 👇 Reclaim the reserved space using negative margins
                // Negative margin = Original_Dimension * (Scale_Factor - 1)
                marginRight: "-317.6px", // 794 * (0.7 - 1)
                marginBottom: "-449.2px", // 1123 * (0.7 - 1)
              }}
            >
              {/* Header */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                {logo && (
                  <Box
                    component="img"
                    src={logo}
                    alt="logo"
                    sx={{ height: 80, mr: 2 }}
                  />
                )}
                <Box sx={{ flexGrow: 1, textAlign: "center" }}>
                  <Typography fontWeight="bold" variant="h3">
                    {meta.companyName}
                  </Typography>
                  <Typography fontSize={14}>{meta.address}</Typography>
                </Box>
              </Box>
              <Divider />

              <Typography
                fontSize={18}
                fontWeight={600}
                sx={{ py: 1, textDecoration: "underline" }}
                align="center"
              >
                Payslip for{" "}
                {new Date(meta.month).toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}
              </Typography>

              {/* Info Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  fontSize: 13,
                  mb: 3,
                }}
              >
                {/* Employee Information */}
                <Box sx={{ flex: 1, width: "50%" }}>
                  <Typography fontWeight={400} sx={{ mb: 0.5 }}>
                    Employee Information
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #e0e0e0",
                      px: 1.2,
                      py: 1,
                      borderRadius: 1,
                      lineHeight: 1,
                      height: "210px",
                      display: "flex",
                      gap: 0.5,
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    {Object.entries(empInfo).map(([k, v]) => (
                      <Box sx={{ display: "flex", gap: 3 }}>
                        <Typography key={k} sx={{ width: "120px" }}>
                          <strong>
                            {k
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())}
                            :
                          </strong>
                        </Typography>
                        <Typography key={k}>{v || "-"}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Bank / ID Information */}
                <Box sx={{ flex: 1, width: "50%" }}>
                  <Typography fontWeight={400} sx={{ mb: 0.5 }}>
                    Bank / ID Information
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #e0e0e0",
                      px: 1.2,
                      py: 1,
                      borderRadius: 1,
                      lineHeight: 1,
                      height: "210px",
                      display: "flex",
                      gap: showESI || showPF || showUAN ? 0.5 : 1,
                      flexDirection: "column",
                      justifyContent:
                        showESI || showPF || showUAN
                          ? "space-between"
                          : "flex-start",
                    }}
                  >
                    {Object.entries(bankInfo)
                      .filter(([k]) => {
                        if (k === "pfNo" && !showPF) return false;
                        if (k === "uan" && !showUAN) return false;
                        if (k === "esiNo" && !showESI) return false;
                        return true;
                      })
                      .map(([k, v]) => (
                        <Box sx={{ display: "flex", gap: 3 }}>
                          <Typography key={k} sx={{ width: "100px" }}>
                            <strong>
                              {k
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())}
                              :
                            </strong>
                          </Typography>
                          <Typography>
                            {v
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase()) || "-"}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                </Box>
              </Box>

              {/* Earnings & Deductions */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                }}
              >
                {/* Earnings */}
                <Box sx={{ flex: 1, width: "50%" }}>
                  <Typography fontWeight={600} sx={{ mb: 0.5, ml: 0.5 }}>
                    Earnings
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: "#f9f9f9" }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Description
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {earnings.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row.desc}
                          </TableCell>
                          <TableCell align="right">{row.amount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                {/* Deductions */}
                <Box sx={{ flex: 1, width: "50%" }}>
                  <Typography fontWeight={600} sx={{ mb: 0.5, ml: 0.5 }}>
                    Deductions
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: "#f9f9f9" }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Description
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deductions.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row.desc}
                          </TableCell>
                          <TableCell align="right">{row.amount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>

              {/* Totals Row (shared line) */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 1,
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flex: 1,
                    bgcolor: "#f9f9f9",
                  }}
                >
                  <Typography fontWeight={700} sx={{ ml: 0.5 }}>
                    Gross Earnings:
                  </Typography>
                  <Typography fontWeight={700} sx={{ mr: 2 }}>
                    ₹{totalEarnings.toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flex: 1,
                    bgcolor: "#f9f9f9",
                  }}
                >
                  <Typography fontWeight={700} sx={{ ml: 0.5 }}>
                    Gross Deductions:
                  </Typography>
                  <Typography fontWeight={700} sx={{ mr: 2 }}>
                    ₹{totalDeductions.toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {/* Summary */}
              <Box sx={{ mt: 4, textAlign: "center", bgcolor: "#f9f9f9" }}>
                {/* <Typography>
            Gross Pay: ₹{totalEarnings.toFixed(2)} | Gross Deduction: ₹
            {totalDeductions.toFixed(2)}
          </Typography> */}
                <Typography
                  fontSize={14}
                  sx={{ mt: 0.5, fontStyle: "italic", color: "#333", py: 1 }}
                >
                  <strong style={{ color: "#000", fontSize: 16 }}>
                    Net Pay: ₹{netPay.toFixed(2)}
                  </strong>{" "}
                  (Rupees {convertNumberToWords(netPay)} only)
                </Typography>
              </Box>
              {!showSignature && (
                <Typography
                  color="text.secondary"
                  fontStyle="italic"
                  sx={{ py: 1 }}
                >
                  General Note: This is a computer generated payslip, Hence
                  Signature not required.
                </Typography>
              )}

              {/* Signature */}
              <Box sx={{ mt: "200px", textAlign: "center", fontSize: 13 }}>
                {showSignature && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 13,
                    }}
                  >
                    <Typography>Signature: ___________________</Typography>
                    <Typography>Date: ____ / ____ / ____</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
      {/* Download Button */}
      <Box
        sx={{
          textAlign: "center",
          display: { xs: "block", md: "none" },
          py: 4,
        }}
      >
        <Button
          variant="contained"
          onClick={handleDownload}
          sx={{ px: 5, py: 1.3, fontWeight: "bold" }}
        >
          Download PDF
        </Button>
      </Box>
    </Box>
  );
}
